import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { signOut } from '../lib/supabase'

const NAV_ITEMS = [
  { id: 'accountability-map', label: 'Accountability Map', icon: OrgIcon, available: true },
  { id: 'scorecard', label: 'Scorecard', icon: ScoreIcon, available: true },
  { id: 'rocks', label: '90 Day Priorities', icon: RocksIcon, available: true },
  { id: 'level-10', label: 'The Weekly', icon: MeetingIcon, available: true },
  { id: 'vto', label: 'The Blueprint', icon: VisionIcon, available: true },
  { id: 'toolbox', label: 'The People Toolbox', icon: ToolboxIcon, available: true },
]

function OrgIcon() {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="6" y="1" width="4" height="3" rx="1" stroke="currentColor" strokeWidth="1.2"/>
    <rect x="1" y="10" width="4" height="3" rx="1" stroke="currentColor" strokeWidth="1.2"/>
    <rect x="6" y="10" width="4" height="3" rx="1" stroke="currentColor" strokeWidth="1.2"/>
    <rect x="11" y="10" width="4" height="3" rx="1" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M8 4V7M8 7H3.5V10M8 7H12.5V10M8 7V10" stroke="currentColor" strokeWidth="1.2"/>
  </svg>
}
function ScoreIcon() {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M5 8h6M5 5.5h6M5 10.5h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
}
function RocksIcon() {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M8 2L10 6H14L11 9L12 13L8 11L4 13L5 9L2 6H6L8 2Z" stroke="currentColor" strokeWidth="1.2"/>
  </svg>
}
function MeetingIcon() {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M8 5V8.5L10.5 10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
}
function VisionIcon() {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M2 8C2 8 4.5 4 8 4C11.5 4 14 8 14 8C14 8 11.5 12 8 12C4.5 12 2 8 2 8Z" stroke="currentColor" strokeWidth="1.2"/>
    <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.2"/>
  </svg>
}
function ToolboxIcon() {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M6 4V2.8C6 2.36 6.36 2 6.8 2H9.2C9.64 2 10 2.36 10 2.8V4" stroke="currentColor" strokeWidth="1.2"/>
    <rect x="2" y="4" width="12" height="9" rx="1" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M2 8h12M7 7v2M9 7v2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
}

function HamburgerIcon() {
  return <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M3 6h14M3 10h14M3 14h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
}

const MOBILE_BREAKPOINT = 768

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  )
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  return isMobile
}

export default function PortalLayout({ children, activePage, onNavigate }) {
  const { user } = useAuth()
  const isMobile = useIsMobile()
  // Desktop: sidebar can collapse to icon rail. Mobile: sidebar is an off-canvas drawer.
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Close mobile drawer whenever the user navigates
  useEffect(() => {
    if (isMobile) setDrawerOpen(false)
  }, [activePage, isMobile])

  // Lock body scroll while mobile drawer is open
  useEffect(() => {
    if (typeof document === 'undefined') return
    document.body.style.overflow = isMobile && drawerOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isMobile, drawerOpen])

  const [companyName, setCompanyName] = useState(
    user?.user_metadata?.company_name || 'Your Company'
  )

  useEffect(() => {
    if (!user?.id) return
    supabase.from('accountability_maps').select('map_data').eq('user_id', user.id).single()
      .then(({ data }) => {
        if (data?.map_data?.companyName) setCompanyName(data.map_data.companyName)
      })
  }, [user?.id])

  const activeLabel = NAV_ITEMS.find(i => i.id === activePage)?.label || 'Hemlock Portal'

  // Compute sidebar visual width + position
  const sidebarVisible = isMobile ? drawerOpen : true
  const sidebarWidth = isMobile ? 280 : (sidebarOpen ? 240 : 64)
  const sidebarShowLabels = isMobile ? true : sidebarOpen

  return (
    <div style={{ display: 'flex', minHeight: '100vh', position: 'relative' }}>
      {/* Mobile top bar */}
      {isMobile && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
          height: 56, background: 'var(--hemlock-green-dark)',
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '0 14px',
          borderBottom: '1px solid rgba(255,255,255,0.06)'
        }}>
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation"
            style={{
              width: 36, height: 36, borderRadius: 6, border: 'none',
              background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.85)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
            }}
          >
            <HamburgerIcon />
          </button>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0
          }}>
            <div style={{
              width: 26, height: 26, borderRadius: 6,
              background: 'var(--hemlock-green)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
            }}>
              <svg width="14" height="14" viewBox="0 0 28 28" fill="none">
                <path d="M14 3L4 8.5V19.5L14 25L24 19.5V8.5L14 3Z" stroke="#C8A96E" strokeWidth="1.4" fill="none"/>
                <path d="M14 3V25M4 8.5L24 19.5M24 8.5L4 19.5" stroke="#C8A96E" strokeWidth="0.8" opacity="0.5"/>
              </svg>
            </div>
            <div style={{ overflow: 'hidden' }}>
              <div style={{
                fontSize: 13, fontWeight: 600, color: 'white', lineHeight: 1.15,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
              }}>{activeLabel}</div>
              <div style={{
                fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 1,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
              }}>{companyName}</div>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop (mobile only, when drawer is open) */}
      {isMobile && drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 90,
            background: 'rgba(20,30,25,0.55)', backdropFilter: 'blur(2px)',
            transition: 'opacity .2s'
          }}
        />
      )}

      {/* Sidebar */}
      <aside style={{
        width: sidebarWidth,
        flexShrink: 0,
        background: 'var(--hemlock-green-dark)',
        display: 'flex', flexDirection: 'column',
        transition: 'width .2s, transform .25s ease',
        overflow: 'hidden',
        // Mobile drawer positioning
        ...(isMobile ? {
          position: 'fixed', top: 0, bottom: 0, left: 0, zIndex: 100,
          transform: sidebarVisible ? 'translateX(0)' : 'translateX(-100%)',
          boxShadow: sidebarVisible ? '6px 0 32px rgba(0,0,0,0.35)' : 'none'
        } : {})
      }}>
        {/* Logo + close button on mobile */}
        <div style={{
          padding: sidebarShowLabels ? '20px 16px 16px 20px' : '24px 16px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', alignItems: 'center', gap: 12,
          minHeight: isMobile ? 56 : 'auto'
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8, flexShrink: 0,
            background: 'var(--hemlock-green)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <svg width="18" height="18" viewBox="0 0 28 28" fill="none">
              <path d="M14 3L4 8.5V19.5L14 25L24 19.5V8.5L14 3Z" stroke="#C8A96E" strokeWidth="1.5" fill="none"/>
              <path d="M14 3V25M4 8.5L24 19.5M24 8.5L4 19.5" stroke="#C8A96E" strokeWidth="1" opacity="0.5"/>
            </svg>
          </div>
          {sidebarShowLabels && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'white', lineHeight: 1.2 }}>Hemlock Group</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 1 }}>Client portal</div>
            </div>
          )}
          {isMobile && (
            <button
              onClick={() => setDrawerOpen(false)}
              aria-label="Close navigation"
              style={{
                width: 32, height: 32, borderRadius: 6, border: 'none',
                background: 'transparent', color: 'rgba(255,255,255,0.55)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M2 2L12 12M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>

        {/* Company pill */}
        {sidebarShowLabels && (
          <div style={{ padding: '12px 20px' }}>
            <div style={{
              background: 'rgba(255,255,255,0.07)', borderRadius: 6,
              padding: '8px 12px', fontSize: 12,
              color: 'rgba(255,255,255,0.7)'
            }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '.06em' }}>Company</div>
              <div style={{ fontWeight: 500, color: 'white', textTransform: 'capitalize' }}>{companyName}</div>
            </div>
          </div>
        )}

        {/* Nav */}
        <nav style={{ flex: 1, padding: '8px 12px', overflowY: 'auto' }}>
          {sidebarShowLabels && (
            <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '.08em', padding: '4px 8px 8px' }}>
              Tools
            </div>
          )}
          {NAV_ITEMS.map(item => {
            const active = activePage === item.id
            return (
              <button
                key={item.id}
                onClick={() => item.available && onNavigate(item.id)}
                title={!sidebarShowLabels ? item.label : ''}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center',
                  gap: 10,
                  padding: sidebarShowLabels ? (isMobile ? '12px 10px' : '9px 10px') : '9px',
                  justifyContent: sidebarShowLabels ? 'flex-start' : 'center',
                  borderRadius: 7, border: 'none', marginBottom: 2,
                  background: active ? 'rgba(200,169,110,0.15)' : 'transparent',
                  color: active ? 'var(--hemlock-gold)' : item.available ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.25)',
                  cursor: item.available ? 'pointer' : 'default',
                  transition: 'background .15s, color .15s',
                  fontSize: 13, fontWeight: active ? 500 : 400,
                  minHeight: isMobile ? 44 : 'auto'
                }}
                onMouseEnter={e => { if (item.available && !active) e.currentTarget.style.background = 'rgba(255,255,255,0.06)' }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}
              >
                <item.icon />
                {sidebarShowLabels && (
                  <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
                )}
                {sidebarShowLabels && !item.available && (
                  <span style={{
                    fontSize: 9, fontWeight: 500, padding: '2px 6px',
                    borderRadius: 999, background: 'rgba(255,255,255,0.08)',
                    color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '.05em'
                  }}>Soon</span>
                )}
              </button>
            )
          })}
        </nav>

        {/* Bottom: collapse + sign out */}
        <div style={{ padding: '12px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          {/* Collapse button — hidden on mobile (drawer pattern doesn't need it) */}
          {!isMobile && (
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{
                width: '100%', padding: '8px', border: 'none', borderRadius: 6,
                background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: sidebarOpen ? 'flex-start' : 'center',
                gap: 8, fontSize: 12, marginBottom: 6
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d={sidebarOpen ? "M9 2L4 7L9 12" : "M5 2L10 7L5 12"} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              {sidebarOpen && 'Collapse'}
            </button>
          )}
          <button
            onClick={signOut}
            style={{
              width: '100%', padding: isMobile ? '12px 8px' : '8px', border: 'none', borderRadius: 6,
              background: 'transparent', color: 'rgba(255,255,255,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: sidebarShowLabels ? 'flex-start' : 'center',
              gap: 8, fontSize: 12,
              minHeight: isMobile ? 44 : 'auto'
            }}
            onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.4)'}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M5 2H2V12H5M9 4L12 7L9 10M12 7H5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
            {sidebarShowLabels && 'Sign out'}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main style={{
        flex: 1, overflow: 'auto', background: 'var(--hemlock-cream)',
        // Push content down past the mobile top bar
        paddingTop: isMobile ? 56 : 0,
        // On mobile we don't subtract sidebar width — the drawer overlays
        minWidth: 0
      }}>
        {children}
      </main>
    </div>
  )
}
